import { createContext, useContext, useState, ReactNode } from 'react';
import en from './en.json';
import nl from './nl.json';

export type Language = 'en' | 'nl';

export interface Translation {
  nav: {
    home: string;
    cv: string;
    contact: string;
  };
  home: {
    companyName: string;
    companyTagline: string;
    companyBio: string;
    focusAreas: string;
    serviceHighlights: string;
    architecture: string;
    architectureDesc: string;
    powerPlatform: string;
    powerPlatformDesc: string;
    development: string;
    developmentDesc: string;
    platform: string;
    platformDesc: string;
    technologies: string;
  };
  cv: {
    title: string;
    subtitle: string;
    filters: string;
    candidateProfile: string;
    loadingProfile: string;
    targetRole: string;
    filterByDate: string;
    fromDate: string;
    industries: string;
    focusAreas: string;
    technologies: string;
    all: string;
    includeCertifications: string;
    generateButton: string;
    generatingButton: string;
    downloadMarkdown: string;
    generatedPreview: string;
    coreSkills: string;
    projectHighlights: string;
    rawMarkdown: string;
    generated: string;
    failedToGenerate: string;
    ongoing: string;
  };
  contact: {
    title: string;
    message: string;
    email: string;
    phone: string;
    location: string;
  };
  footer: string;
  seo: {
    siteName: string;
    homeTitle: string;
    homeDescription: string;
    cvTitle: string;
    cvDescription: string;
    contactTitle: string;
    contactDescription: string;
  };
  errors: {
    failedToLoadProfile: string;
  };
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translation;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, Translation> = {
  en: en as unknown as Translation,
  nl: nl as unknown as Translation,
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>('en');

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
